import type {
  DatePreset,
  GridColumn,
  GridFilter,
  GridPreferences,
} from "@/modules/shared/dataGrid/gridTypes";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  this_week: "This week",
  this_month: "This month",
  last_30_days: "Last 30 days",
  custom: "Custom range",
};

/** Inclusive [from, to] dates (YYYY-MM-DD) for a date filter. */
export const dateFilterRange = (
  filter: Extract<GridFilter, { kind: "date" }>,
  now = new Date(),
): { from: string | null; to: string | null } => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (filter.preset) {
    case "today":
      return { from: ymd(today), to: ymd(today) };
    case "this_week": {
      const start = new Date(today);
      // Weeks start on Monday.
      start.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: ymd(start), to: ymd(end) };
    }
    case "this_month":
      return {
        from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: ymd(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case "last_30_days": {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return { from: ymd(start), to: ymd(today) };
    }
    case "custom":
      return { from: filter.from ?? null, to: filter.to ?? null };
  }
};

const nextDay = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  return ymd(new Date(y, m - 1, d + 1));
};

const fieldOf = <Row>(column: GridColumn<Row>) => column.field ?? column.id;

/**
 * Filters → PostgREST (ra-data-postgrest) filter values for server lists.
 * Keys use `field@operator`; a date range ends before the next day so
 * timestamps on the last day are included.
 */
export const toServerFilters = <Row>(
  filters: GridFilter[] | undefined,
  columns: GridColumn<Row>[],
  now = new Date(),
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const filter of filters ?? []) {
    const column = columns.find((c) => c.id === filter.columnId);
    if (!column) continue;
    const field = fieldOf(column);
    switch (filter.kind) {
      case "text":
        // The PostgREST adapter wraps ilike values in *…* (per word).
        if (filter.contains.trim()) {
          out[`${field}@ilike`] = filter.contains.trim();
        }
        break;
      case "select":
        if (filter.values.length) {
          out[`${field}@in`] =
            `(${filter.values.map((v) => `"${v.replace(/"/g, "")}"`).join(",")})`;
        }
        break;
      case "date": {
        const { from, to } = dateFilterRange(filter, now);
        if (from) out[`${field}@gte`] = from;
        if (to) out[`${field}@lt`] = nextDay(to);
        break;
      }
      case "range":
        if (filter.min != null) out[`${field}@gte`] = filter.min;
        if (filter.max != null) out[`${field}@lte`] = filter.max;
        break;
      case "boolean":
        out[`${field}@eq`] = filter.value;
        break;
      case "empty":
        if (filter.empty) out[`${field}@is`] = null;
        else out[`${field}@not.is`] = null;
        break;
    }
  }
  return out;
};

/** Filter keys a grid may have written (to replace them on change). */
export const isGridFilterKey = <Row>(key: string, columns: GridColumn<Row>[]) =>
  columns.some((column) => key.startsWith(`${fieldOf(column)}@`));

const rawValue = <Row>(row: Row, column: GridColumn<Row>) =>
  column.value
    ? column.value(row)
    : (row as Record<string, unknown>)[fieldOf(column)];

const isBlank = (value: unknown) =>
  value == null || (typeof value === "string" && value.trim() === "");

/** Same filters on rows already loaded (small tables, record tabs). */
export const applyClientFilters = <Row>(
  rows: Row[],
  filters: GridFilter[] | undefined,
  columns: GridColumn<Row>[],
  now = new Date(),
) => {
  const active = (filters ?? []).filter((filter) =>
    columns.some((c) => c.id === filter.columnId),
  );
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every((filter) => {
      const column = columns.find((c) => c.id === filter.columnId)!;
      const value = rawValue(row, column);
      switch (filter.kind) {
        case "text":
          return String(value ?? "")
            .toLowerCase()
            .includes(filter.contains.trim().toLowerCase());
        case "select":
          return (
            !filter.values.length || filter.values.includes(String(value ?? ""))
          );
        case "date": {
          if (isBlank(value)) return false;
          const day = String(value).slice(0, 10);
          const { from, to } = dateFilterRange(filter, now);
          return (!from || day >= from) && (!to || day <= to);
        }
        case "range": {
          const n = Number(value);
          if (!Number.isFinite(n)) return false;
          return (
            (filter.min == null || n >= filter.min) &&
            (filter.max == null || n <= filter.max)
          );
        }
        case "boolean":
          return Boolean(value) === filter.value;
        case "empty":
          return isBlank(value) === filter.empty;
      }
    }),
  );
};

/** Columns in the member's order, without the ones they hid. */
export const visibleColumns = <Row>(
  columns: GridColumn<Row>[],
  prefs: GridPreferences | undefined,
) => {
  const hidden = new Set(prefs?.hidden ?? []);
  const order = prefs?.order ?? [];
  const rank = (id: string) => {
    const index = order.indexOf(id);
    return index === -1
      ? order.length + columns.findIndex((c) => c.id === id)
      : index;
  };
  return [...columns]
    .filter((column) => !column.filterOnly)
    .sort((a, b) => rank(a.id) - rank(b.id))
    .filter((column) =>
      column.alwaysVisible
        ? true
        : prefs?.hidden
          ? !hidden.has(column.id)
          : !column.defaultHidden,
    );
};

/** Human label of a filter chip, e.g. "Created: This week". */
export const filterChipLabel = <Row>(
  filter: GridFilter,
  column: GridColumn<Row>,
) => {
  switch (filter.kind) {
    case "text":
      return `${column.label} contains "${filter.contains}"`;
    case "select": {
      const labels = filter.values.map(
        (v) => column.options?.find((o) => o.value === v)?.label ?? v,
      );
      return `${column.label}: ${labels.join(", ")}`;
    }
    case "date":
      return filter.preset === "custom"
        ? `${column.label}: ${filter.from ?? "…"} – ${filter.to ?? "…"}`
        : `${column.label}: ${DATE_PRESET_LABELS[filter.preset]}`;
    case "range":
      return `${column.label}: ${filter.min ?? "…"} – ${filter.max ?? "…"}`;
    case "boolean":
      return `${column.label}: ${filter.value ? "Yes" : "No"}`;
    case "empty":
      return `${column.label}: ${filter.empty ? "Empty" : "Not empty"}`;
  }
};

/** Sort loaded rows by a column's value (blanks last). */
export const sortClientRows = <Row>(
  rows: Row[],
  sort: { field: string; order: "ASC" | "DESC" } | null | undefined,
  columns: GridColumn<Row>[],
) => {
  if (!sort) return rows;
  const column = columns.find(
    (c) => (c.sortField ?? c.field ?? c.id) === sort.field,
  );
  if (!column) return rows;
  const valueOf = (row: Row) =>
    column.value
      ? column.value(row)
      : (row as Record<string, unknown>)[sort.field];
  const direction = sort.order === "ASC" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (isBlank(va) && isBlank(vb)) return 0;
    if (isBlank(va)) return 1;
    if (isBlank(vb)) return -1;
    if (typeof va === "number" && typeof vb === "number") {
      return (va - vb) * direction;
    }
    return (
      String(va).localeCompare(String(vb), undefined, { numeric: true }) *
      direction
    );
  });
};
